/**
 * Download Progress Overlay
 *
 * Shows download status on hover (configurable, debug feature)
 * Displays: active downloads, progress, chunk status, queue info
 */

export interface DownloadOverlayConfig {
  enabled: boolean;
  updateInterval?: number; // ms between updates
  autoHide?: boolean; // Hide when no downloads
}

export class DownloadOverlay {
  private overlay: HTMLElement | null = null;
  private config: DownloadOverlayConfig;
  private updateTimer: number | null = null;
  private _visible: boolean = false; // User-toggled visibility (D key)
  private _getProgress: (() => Record<string, any>) | null = null;

  constructor(config: DownloadOverlayConfig) {
    this.config = {
      updateInterval: 1000,
      autoHide: true,
      ...config
    };

    if (this.config.enabled) {
      this.createOverlay();
      // Start hidden — only shown when downloads are active or user presses D
      this.overlay!.style.display = 'none';
    }
  }

  private createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'download-overlay';
    // Style like top status messages - always visible, clean design
    this.overlay.style.cssText = `
      position: fixed;
      top: 1.5vh;
      left: 1.5vw;
      background: rgba(0, 0, 0, 0.88);
      color: #fff;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 1.4vw;
      padding: 1vh 1.2vw;
      border-radius: 0.4vw;
      border: 1px solid rgba(255, 255, 255, 0.25);
      z-index: 999999;
      max-width: 35vw;
      box-shadow: 0 0.3vh 1.2vw rgba(0, 0, 0, 0.5);
    `;

    document.body.appendChild(this.overlay);
  }

  /**
   * Set the progress callback. Called by PwaPlayer after DownloadManager is created.
   */
  public setProgressCallback(fn: () => Record<string, any>) {
    this._getProgress = fn;
  }

  private updateOverlay() {
    if (!this.overlay) return;

    const progress = this._getProgress ? this._getProgress() : {};
    const html = this.renderStatus(progress);
    const hasDownloads = !!html;

    if (hasDownloads) {
      // Active downloads — show overlay (auto or user-toggled)
      this.overlay.innerHTML = html;
      this.overlay.style.display = 'block';
    } else if (this._visible) {
      // User toggled on (D key) but no downloads — show idle status
      this.overlay.innerHTML = '<div style="color: #6c6; font-size: 1.4vw;">✓ All downloads complete</div>';
      this.overlay.style.display = 'block';
    } else {
      // No downloads and not user-toggled — stop polling, hide
      this.stopUpdating();
      this.overlay.style.display = 'none';
    }
  }

  private renderStatus(progress: any): string {
    const downloads = progress || {};

    if (Object.keys(downloads).length === 0) {
      if (this.config.autoHide) {
        return ''; // Hide when no downloads
      }
      return `<div style="color: #6c6;">✓ No downloads</div>`;
    }

    const numDownloads = Object.keys(downloads).length;
    let html = `<div style="font-weight: 600; margin-bottom: 0.8vh; font-size: 1.4vw;">Downloads: ${numDownloads} active</div>`;

    for (const [url, progress] of Object.entries(downloads)) {
      const filename = this.extractFilename(url);
      const percent = Math.round((progress as any).percent || 0);
      const downloaded = this.formatBytes((progress as any).downloaded || 0);
      const total = this.formatBytes((progress as any).total || 0);

      html += `
        <div style="margin-bottom: 0.6vh; padding-bottom: 0.6vh; border-bottom: 1px solid rgba(255,255,255,0.1);">
          <div style="font-size: 1.2vw; margin-bottom: 0.2vh;">${filename}</div>
          <div style="background: rgba(255,255,255,0.1); height: 0.4vh; border-radius: 0.2vw; overflow: hidden;">
            <div style="width: ${percent}%; height: 100%; background: #4a9eff; transition: width 0.3s;"></div>
          </div>
          <div style="color: #999; font-size: 1.1vw; margin-top: 0.2vh;">
            ${percent}% · ${downloaded} / ${total}
          </div>
        </div>
      `;
    }

    return html;
  }

  private extractFilename(key: string): string {
    // Key is now "type/id" (e.g. "media/5", "layout/12") — no URL parsing needed
    return key || 'unknown';
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
  }

  /**
   * Toggle overlay visibility (D key).
   * When toggled on, starts polling. When toggled off, hides immediately.
   */
  public toggle() {
    if (!this.overlay) return;
    this._visible = !this._visible;
    if (this._visible) {
      this.overlay.style.display = 'block';
      this.updateOverlay(); // Immediate update
      this.startUpdating();
    } else {
      this.overlay.style.display = 'none';
      this.stopUpdating();
    }
  }

  /**
   * Start polling for download progress.
   * Safe to call multiple times — won't create duplicate timers.
   * Does NOT set _visible — the overlay auto-shows when downloads are active
   * and auto-hides when they finish. Use toggle() for user-controlled visibility.
   */
  public startUpdating() {
    if (this.updateTimer) return; // Already polling
    this.updateTimer = window.setInterval(() => {
      this.updateOverlay();
    }, this.config.updateInterval);
    this.updateOverlay(); // Immediate first update
  }

  /**
   * Stop polling. Called automatically when no downloads are active.
   */
  private stopUpdating() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
  }

  public destroy() {
    this.stopUpdating();
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  public setEnabled(enabled: boolean) {
    this.config.enabled = enabled;

    if (enabled && !this.overlay) {
      this.createOverlay();
      // Polling starts on demand via startUpdating()
    } else if (!enabled && this.overlay) {
      this.destroy();
    }
  }
}

/**
 * Get default configuration based on environment
 */
export function getDefaultOverlayConfig(): DownloadOverlayConfig {
  // Check URL parameter override
  const urlParams = new URLSearchParams(window.location.search);
  const showDownloads = urlParams.get('showDownloads');

  if (showDownloads !== null) {
    return { enabled: showDownloads !== '0' && showDownloads !== 'false' };
  }

  // Check localStorage preference
  const savedPref = localStorage.getItem('xibo_show_download_overlay');
  if (savedPref !== null) {
    return { enabled: savedPref === 'true' };
  }

  // Default: disabled — toggle with D key or ?showDownloads=1
  return { enabled: false };
}
